import i18next from 'i18next';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import { generateNewPageable } from 'src/adminPanel/utils/helperClasses';
import {
    createNewCommissionInvoiceFilterCall,
    deleteCommissionInvoiceFilterCall,
    getCommissionInvoiceDetails,
    getCommissionInvoiceDetailsPdf,
    getCommissionInvoiceFilters,
    getCommissionInvoicePdf,
    getCommissionInvoicesByFilter,
    getCommissionInvoicesByFilterId,
    updateCommissionInvoiceFilterCall,
} from 'src/api/admin-commission-invoice';
import { enqueueSnackbarAction } from '../notifications/actions';
import {
    CLEAR_COMMISSION_INVOICE_DETAILS,
    CREATE_NEW_COMMISSION_INVOICE_FILTER,
    DELETE_COMMISSION_INVOICE_FILTER,
    fetchCommissionInvoicesByFilter,
    fetchCommissionInvoicesByFilterId,
    FETCH_COMMISSION_INVOICES_BY_FILTER,
    FETCH_COMMISSION_INVOICES_BY_FILTER_ID,
    FETCH_COMMISSION_INVOICE_DETAILS,
    FETCH_COMMISSION_INVOICE_DETAILS_PDF,
    FETCH_COMMISSION_INVOICE_FILTERS,
    FETCH_COMMISSION_INVOICE_PDF,
    requestClearCommissionInvoiceDetails,
    requestCreateNewCommissionInvoiceFilter,
    requestCreateNewCommissionInvoiceFilterFailure,
    requestCreateNewCommissionInvoiceFilterSuccess,
    requestDeleteCommissionInvoiceFilter,
    requestDeleteCommissionInvoiceFilterFailure,
    requestDeleteCommissionInvoiceFilterSuccess,
    requestFetchCommissionInvoiceDetails,
    requestFetchCommissionInvoiceDetailsFailure,
    requestFetchCommissionInvoiceDetailsPdf,
    requestFetchCommissionInvoiceDetailsPdfFailure,
    requestFetchCommissionInvoiceDetailsPdfSuccess,
    requestFetchCommissionInvoiceDetailsSuccess,
    requestFetchCommissionInvoiceFilters,
    requestFetchCommissionInvoiceFiltersFailure,
    requestFetchCommissionInvoiceFiltersSuccess,
    requestFetchCommissionInvoicePdf,
    requestFetchCommissionInvoicePdfFailure,
    requestFetchCommissionInvoicePdfSuccess,
    requestFetchCommissionInvoicesByFilter,
    requestFetchCommissionInvoicesByFilterFailure,
    requestFetchCommissionInvoicesByFilterId,
    requestFetchCommissionInvoicesByFilterIdFailure,
    requestFetchCommissionInvoicesByFilterIdSuccess,
    requestFetchCommissionInvoicesByFilterSuccess,
    requestUpdateCommissionInvoiceFilter,
    requestUpdateCommissionInvoiceFilterFailure,
    requestUpdateCommissionInvoiceFilterSuccess,
    UPDATE_COMMISSION_INVOICE_FILTER,
} from './actions';

function* fetchCommissionInvoiceFilterSaga(): SagaIterator {
    yield put(requestFetchCommissionInvoiceFilters());

    const { error, response } = yield call(getCommissionInvoiceFilters);

    if (error) {
        yield put(requestFetchCommissionInvoiceFiltersFailure(error));
    } else {
        yield put(requestFetchCommissionInvoiceFiltersSuccess(response));
    }
}

export function* generateFetchCommissionInvoiceFilterWatcher() {
    yield takeLatest(FETCH_COMMISSION_INVOICE_FILTERS, fetchCommissionInvoiceFilterSaga);
}

function* fetchCommissionInvoicesByFilterSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchCommissionInvoicesByFilter());

    const { error, response } = yield call(getCommissionInvoicesByFilter, action.pageable, action.filter);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('offsetting:pages.commissionInvoice.message.fetchCommissionInvoices.error') +
                    ' ' +
                    error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestFetchCommissionInvoicesByFilterFailure(error));
    } else {
        if (response.data.content.length === 0 && response.data.numberOfElements > 0) {
            yield put(fetchCommissionInvoicesByFilter(action.filter, generateNewPageable(action.pageable)));
        } else {
            yield put(
                enqueueSnackbarAction({
                    message: i18next.t('offsetting:pages.commissionInvoice.message.fetchCommissionInvoices.success'),
                    options: {
                        key: new Date().getTime() + Math.random(),
                        variant: 'success',
                    },
                })
            );
            yield put(requestFetchCommissionInvoicesByFilterSuccess(response));
        }
    }
}

export function* generateFetchCommissionInvoicesByFilterWatcher() {
    yield takeLatest(FETCH_COMMISSION_INVOICES_BY_FILTER, fetchCommissionInvoicesByFilterSaga);
}

function* fetchCommissionInvoicesByFilterIdSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchCommissionInvoicesByFilterId());

    const { error, response } = yield call(getCommissionInvoicesByFilterId, action.filterId, action.pageable);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.message.fetchCommissionInvoices.error'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestFetchCommissionInvoicesByFilterIdFailure(error));
    } else {
        if (response.data.content.length === 0 && response.data.numberOfElements > 0) {
            yield put(fetchCommissionInvoicesByFilterId(action.filterId, generateNewPageable(action.pageable)));
        } else {
            yield put(
                enqueueSnackbarAction({
                    message: i18next.t('offsetting:pages.commissionInvoice.message.fetchCommissionInvoices.success'),
                    options: {
                        key: new Date().getTime() + Math.random(),
                        variant: 'success',
                    },
                })
            );
            yield put(requestFetchCommissionInvoicesByFilterIdSuccess(response));
        }
    }
}

export function* generateFetchCommissionInvoicesByFilterIdWatcher() {
    yield takeLatest(FETCH_COMMISSION_INVOICES_BY_FILTER_ID, fetchCommissionInvoicesByFilterIdSaga);
}

function* fetchCommissionInvoicePdfSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchCommissionInvoicePdf());

    const { error, response } = yield call(getCommissionInvoicePdf, action.commissionInvoiceId);

    if (error) {
        yield put(requestFetchCommissionInvoicePdfFailure(error));
    } else {
        yield put(requestFetchCommissionInvoicePdfSuccess(response));
    }
}

export function* generateFetchCommissionInvoicePdfWatcher() {
    yield takeLatest(FETCH_COMMISSION_INVOICE_PDF, fetchCommissionInvoicePdfSaga);
}

function* fetchCommissionInvoiceDetailsPdf(action: AnyAction): SagaIterator {
    yield put(requestFetchCommissionInvoiceDetailsPdf());

    const { error, response } = yield call(getCommissionInvoiceDetailsPdf, action.commissionInvoiceId);

    if (error) {
        yield put(requestFetchCommissionInvoiceDetailsPdfFailure(error));
    } else {
        yield put(requestFetchCommissionInvoiceDetailsPdfSuccess(response));
    }
}

export function* generateFetchCommissionInvoiceDetailsPdfWatcher() {
    yield takeLatest(FETCH_COMMISSION_INVOICE_DETAILS_PDF, fetchCommissionInvoiceDetailsPdf);
}

function* createNewCommissionInvoiceFilterSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewCommissionInvoiceFilter());

    const { error, response } = yield call(createNewCommissionInvoiceFilterCall, action.filter);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.filter.createFilter.error'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestCreateNewCommissionInvoiceFilterFailure(error));
    } else {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.filter.createFilter.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(requestCreateNewCommissionInvoiceFilterSuccess(response));
    }
}

export function* generateCreateNewCommissionInvoiceFilterWatcher() {
    yield takeLatest(CREATE_NEW_COMMISSION_INVOICE_FILTER, createNewCommissionInvoiceFilterSaga);
}

function* updateCommissionInvoiceFilterSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateCommissionInvoiceFilter());

    const { error, response } = yield call(updateCommissionInvoiceFilterCall, action.filter);

    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.filter.updateFilter.error'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
        yield put(requestUpdateCommissionInvoiceFilterFailure(error));
    } else {
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.filter.updateFilter.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(requestUpdateCommissionInvoiceFilterSuccess(response));
    }
}

export function* generateUpdateCommissionInvoiceFilterWatcher() {
    yield takeLatest(UPDATE_COMMISSION_INVOICE_FILTER, updateCommissionInvoiceFilterSaga);
}

function* deleteCommissionInvoiceFilterSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteCommissionInvoiceFilter());

    const { error, response } = yield call(deleteCommissionInvoiceFilterCall, action.filter);

    if (error) {
        yield put(requestDeleteCommissionInvoiceFilterFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.filter.deleteFilter.error'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestDeleteCommissionInvoiceFilterSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.filter.deleteFilter.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateDeleteCommissionInvoiceFilterWatcher() {
    yield takeLatest(DELETE_COMMISSION_INVOICE_FILTER, deleteCommissionInvoiceFilterSaga);
}

function* fetchCommissionInvoiceDetailsSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchCommissionInvoiceDetails());

    const { error, response } = yield call(getCommissionInvoiceDetails, action.commissionInvoiceId);

    if (error) {
        yield put(requestFetchCommissionInvoiceDetailsFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.message.fetchCommissionInvoiceDetails.error'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestFetchCommissionInvoiceDetailsSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.commissionInvoice.message.fetchCommissionInvoiceDetails.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
    }
}

export function* generateFetchCommissionInvoiceDetailsWatcher() {
    yield takeLatest(FETCH_COMMISSION_INVOICE_DETAILS, fetchCommissionInvoiceDetailsSaga);
}

function* clearCommissionInvoiceDetailsSaga(action: AnyAction): SagaIterator {
    yield put(requestClearCommissionInvoiceDetails());
}

export function* generateClearCommissionInvoiceDetailsWatcher() {
    yield takeLatest(CLEAR_COMMISSION_INVOICE_DETAILS, clearCommissionInvoiceDetailsSaga);
}
